import merge from 'lodash.merge';
import { scrub_url_string } from './privacy';

function build_url_parameter_object(url_search_params) {
	const url_params = {};
	url_search_params.forEach((value, key) => {
		url_params[key] = value;
	});
	return url_params;
}

function get_meta_content(selector) {
	try {
		return document.querySelector(selector)?.getAttribute('content') || null;
	} catch {
		return null;
	}
}

function get_referring_domain(referrer) {
	if (!referrer) {
		return null;
	}
	try {
		return new URL(referrer).host || null;
	} catch {
		return null;
	}
}

function build_url_or_fallback(url_value) {
	try {
		return new URL(url_value || window.location.href, window.location.href);
	} catch {
		return new URL(window.location.href);
	}
}

/**
 * Resolves the raw page URL to scrub. Prefers a per-event snapshot (set at capture time by
 * build_base_analytics_event via build_page_metadata) so batch and per-event metadata reflect
 * the URL where the event fired, not the URL at flush time.
 */
function resolve_raw_page_url(data_hub_instance, event_metadata) {
	if (event_metadata && typeof event_metadata.page_url === 'string' && event_metadata.page_url) {
		return event_metadata.page_url;
	}
	if (typeof window !== 'undefined' && window.location?.href) {
		return window.location.href;
	}
	return data_hub_instance?.page_metadata?.page_url || null;
}

export function build_page_metadata(data_hub_instance = window?.data_hub, event_metadata = {}) {
	const raw_page_url = resolve_raw_page_url(data_hub_instance, event_metadata);
	const location_url = build_url_or_fallback(raw_page_url);
	const scrubbed_page_url = scrub_url_string(location_url.toString());
	const scrubbed_referrer = scrub_url_string(document.referrer || '');
	const scrubbed_url = build_url_or_fallback(scrubbed_page_url);
	const url_params = build_url_parameter_object(scrubbed_url.searchParams);
	// Refcodes and iterable ids are populated by set_page_metadata_defaults in
	// data_hub_metadata.js, which runs synchronously in the constructor before
	// init_analytics_bootstrap and well before any analytics flush. Falling back to
	// url_params here covers SPA route changes that introduce new params after init.
	const base_metadata = {
		page_url: scrubbed_page_url,
		canonical_url: document.querySelector('link[rel="canonical"]')?.href || null,
		origin: location_url.origin,
		protocol: location_url.protocol,
		host: location_url.host,
		pathname: location_url.pathname,
		search: scrubbed_url.search,
		hash: location_url.hash || '',
		referer: scrubbed_referrer,
		referring_domain: get_referring_domain(scrubbed_referrer),
		page_title: document.title || data_hub_instance?.page_metadata?.page_title || get_meta_content('meta[name="title"]') || null,
		page_description: get_meta_content('meta[name="description"]'),
		og_title: get_meta_content('meta[property="og:title"]'),
		og_description: get_meta_content('meta[property="og:description"]'),
		og_image: get_meta_content('meta[property="og:image"]'),
		content_language: document.documentElement?.lang || null,
		url_params,
		utm_source: url_params.utm_source || null,
		utm_medium: url_params.utm_medium || null,
		utm_campaign: url_params.utm_campaign || null,
		utm_term: url_params.utm_term || null,
		utm_content: url_params.utm_content || null,
		utm_id: url_params.utm_id || null,
		gclid: url_params.gclid || null,
		fbclid: url_params.fbclid || null,
		msclkid: url_params.msclkid || null,
		refcode: url_params.refcode || data_hub_instance?.page_metadata?.refcodes?.refcode || null,
		refcode2: url_params.refcode2 || data_hub_instance?.page_metadata?.refcodes?.refcode2 || null,
		refcodes: data_hub_instance?.page_metadata?.refcodes || {},
		iterableCampaignId: data_hub_instance?.iterableCampaignId || null,
		iterableTemplateId: data_hub_instance?.iterableTemplateId || null,
		attribution_touch: null,
		is_spa_navigation: false,
		navigation_type: typeof performance !== 'undefined' ? performance.getEntriesByType?.('navigation')?.[0]?.type || null : null
	};

	return merge(
		{},
		data_hub_instance?.page_metadata || {},
		base_metadata,
		event_metadata || {}
	);
}

/**
 * Keeps data_hub_instance.page_metadata aligned with the current document after an SPA route
 * change. Uses the same query-stripped page_url convention as set_page_metadata_defaults so
 * form submits stay privacy-safe while analytics events carry the full scrubbed URL.
 *
 * @param {Object} data_hub_instance
 * @returns {void}
 */
export function sync_instance_page_metadata_from_current_location(data_hub_instance) {
	if (!data_hub_instance?.page_metadata || typeof window === 'undefined') {
		return;
	}
	const fresh_metadata = build_page_metadata(data_hub_instance, {});
	if (typeof fresh_metadata.page_url === 'string' && fresh_metadata.page_url) {
		data_hub_instance.page_metadata.page_url = fresh_metadata.page_url.split('?')[0];
	}
	if (typeof fresh_metadata.page_title === 'string' && fresh_metadata.page_title) {
		data_hub_instance.page_metadata.page_title = fresh_metadata.page_title;
	}
}
